import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { OrgProvider } from './contexts/OrgContext';
import { Box, CircularProgress } from '@mui/material';
import { ColorModeContext } from './contexts/ColorModeContext';
import { getDesignTokens } from './theme/theme';
import { BILLING_ENABLED } from './config/features';

// Always-needed components (static imports — keep bundle small for initial load)
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

// Heavy components — lazy-loaded on demand (code splitting)
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const LoginPage = React.lazy(() => import('./components/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('./components/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./components/ResetPasswordPage'));
const LandingPage = React.lazy(() => import('./components/LandingPage'));
const AnalysisPage = React.lazy(() => import('./components/AnalysisPage'));
const LandingPageV2 = React.lazy(() => import('./components/LandingPageV2'));
const ComparisonPage = React.lazy(() => import('./components/ComparisonPage'));
const MarketingPage = React.lazy(() => import('./components/MarketingPage'));
const ContactPage = React.lazy(() => import('./components/ContactPage'));
const OrganizationPage = React.lazy(() => import('./components/OrganizationPage'));
const ProgramDetail = React.lazy(() => import('./components/ProgramDetail'));
const ProjectDetail = React.lazy(() => import('./components/ProjectDetail'));
const JobDetail = React.lazy(() => import('./components/JobDetail'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const TermsOfServicePage = React.lazy(() => import('./components/TermsOfServicePage'));
const PrivacyPolicyPage = React.lazy(() => import('./components/PrivacyPolicyPage'));
const WhatWeDoPage = React.lazy(() => import('./components/WhatWeDoPage'));
const NepaLandingPage = React.lazy(() => import('./components/NepaLandingPage'));
const ProfilePage = React.lazy(() => import('./components/ProfilePage'));
const RateSheetGenerator = React.lazy(() => import('./components/RateSheetGenerator'));
const PlaybookManager = React.lazy(() => import('./pages/PlaybookManager'));

const Help = React.lazy(() => import('./components/Help'));
const DesignLab = React.lazy(() => import('./components/DesignLab'));
const AnalysisToolsPage = React.lazy(() => import('./components/AnalysisToolsPage'));
const LocateDashboard = React.lazy(() => import('./components/LocateDashboard'));
const PermittingHub = React.lazy(() => import('./components/PermittingHub'));
const NEPAHub = React.lazy(() => import('./components/NEPAHub'));
const FieldOperationsHub = React.lazy(() => import('./components/FieldOperationsHub'));
const TrackerHub = React.lazy(() => import('./components/TrackerHub'));
const TrackerDetail = React.lazy(() => import('./components/TrackerDetail'));
const TrackerFormPublic = React.lazy(() => import('./components/TrackerFormPublic'));

// Loading fallback for lazy components
const LazyFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <CircularProgress />
  </Box>
);

function App() {
  const [mode, setMode] = React.useState(() => {
    const savedMode = localStorage.getItem('colorMode');
    return savedMode || 'dark';
  });

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('colorMode', newMode);
          return newMode;
        });
      },
      mode,
    }),
    [mode],
  );

  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <OrgProvider>
          <Router>
            <Layout>
              <Suspense fallback={<LazyFallback />}>
              <Routes>
                <Route path="/design-lab" element={<DesignLab />} />
                <Route path="/" element={<LandingPage />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/analyze/:moduleId" element={
                  <ProtectedRoute>
                    <AnalysisPage />
                  </ProtectedRoute>
                } />
                <Route path="/tools" element={
                  <ProtectedRoute>
                    <AnalysisToolsPage />
                  </ProtectedRoute>
                } />
                <Route path="/programs/:programId" element={
                  <ProtectedRoute>
                    <ProgramDetail />
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId" element={
                  <ProtectedRoute>
                    <ProjectDetail />
                  </ProtectedRoute>
                } />
                <Route path="/jobs/:jobId" element={
                  <ProtectedRoute>
                    <JobDetail />
                  </ProtectedRoute>
                } />
                <Route path="/organization" element={
                  <ProtectedRoute>
                    <OrganizationPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <Navigate to="/organization" replace />
                } />
                <Route path="/permitting" element={
                  <ProtectedRoute>
                    <PermittingHub />
                  </ProtectedRoute>
                } />
                {/* Legacy redirects */}
                <Route path="/permit-research" element={<Navigate to="/permitting" replace />} />
                <Route path="/row-permitting" element={<Navigate to="/permitting" replace />} />
                <Route path="/nepa" element={
                  <ProtectedRoute>
                    <NEPAHub />
                  </ProtectedRoute>
                } />
                <Route path="/nepa/:reviewId" element={
                  <ProtectedRoute>
                    <NEPAHub />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } />
                {!BILLING_ENABLED && (
                  <>
                    <Route path="/billing/*" element={<Navigate to="/dashboard" replace />} />

                  </>
                )}
                <Route element={<AdminRoute />}>
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/playbooks" element={
                    <ProtectedRoute>
                      <PlaybookManager />
                    </ProtectedRoute>
                  } />
                </Route>
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/what-we-do" element={<WhatWeDoPage />} />
                <Route path="/bead-nepa" element={<NepaLandingPage />} />
                <Route path="/landing-v2" element={<LandingPageV2 />} />
                <Route path="/vs" element={<ComparisonPage />} />
                <Route path="/vs/vitruvi" element={<ComparisonPage comparison="vitruvi" />} />
                <Route path="/vs/ineight" element={<ComparisonPage comparison="ineight" />} />
                <Route path="/vs/spreadsheet-gis" element={<ComparisonPage comparison="spreadsheet-gis" />} />
                <Route path="/pricing" element={<MarketingPage page="pricing" />} />
                <Route path="/about" element={<MarketingPage page="about" />} />
                <Route path="/customers" element={<MarketingPage page="customers" />} />
                <Route path="/features" element={<MarketingPage page="features" />} />
                <Route path="/utility-construction-intelligence" element={<MarketingPage page="category" />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/rate-sheet-generator" element={
                  <ProtectedRoute>
                    <RateSheetGenerator />
                  </ProtectedRoute>
                } />
                <Route path="/field" element={
                  <ProtectedRoute>
                    <FieldOperationsHub />
                  </ProtectedRoute>
                } />
                <Route path="/locate" element={
                  <ProtectedRoute>
                    <LocateDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/tracking" element={
                  <ProtectedRoute>
                    <TrackerHub />
                  </ProtectedRoute>
                } />
                <Route path="/tracking/:trackerId" element={
                  <ProtectedRoute>
                    <TrackerDetail />
                  </ProtectedRoute>
                } />
                <Route path="/forms/:token" element={<TrackerFormPublic />} />
                <Route path="/help" element={
                  <ProtectedRoute>
                    <Help />
                  </ProtectedRoute>
                } />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Routes>
              </Suspense>
            </Layout>
          </Router>
        </OrgProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;

